// src/components/album/MediaApprovalModal.jsx

import React, { useState, useEffect } from 'react';
import { 
  X, 
  CheckCircle, 
  XCircle, 
  Clock, 
  User, 
  Calendar,
  Image as ImageIcon,
  Film,
  AlertCircle,
  Eye,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { albumService } from '../../services/albumService';
import { useAuth } from '../../contexts/AuthContext';

export const MediaApprovalModal = ({ bookId, onClose, onApprovalComplete }) => {
  const { user } = useAuth();
  const [pendingMedia, setPendingMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  useEffect(() => {
    loadPendingMedia();
  }, [bookId]);

  const loadPendingMedia = async () => {
    try {
      setLoading(true);
      const pending = await albumService.getPendingMedia(bookId);
      setPendingMedia(pending);
    } catch (error) {
      console.error('Error loading pending media:', error);
    } finally {
      setLoading(false);
    }
  };

  const currentMedia = pendingMedia[currentIndex];

  const handleApprove = async () => {
    if (!currentMedia) return;

    setProcessing(true);
    try {
      await albumService.approveMedia(
        bookId,
        currentMedia.galleryId,
        currentMedia.id,
        user.uid
      );

      // Remove from pending list
      const newPending = pendingMedia.filter((_, i) => i !== currentIndex);
      setPendingMedia(newPending);

      // Adjust index if needed
      if (currentIndex >= newPending.length && newPending.length > 0) {
        setCurrentIndex(newPending.length - 1);
      }

      if (onApprovalComplete) {
        onApprovalComplete();
      }
    } catch (error) {
      console.error('Error approving media:', error);
      alert('Failed to approve media. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!currentMedia) return;

    setProcessing(true);
    try {
      await albumService.rejectMedia(
        bookId,
        currentMedia.galleryId,
        currentMedia.id,
        user.uid,
        rejectReason
      );

      // Remove from pending list
      const newPending = pendingMedia.filter((_, i) => i !== currentIndex);
      setPendingMedia(newPending);

      // Reset reject form
      setShowRejectForm(false);
      setRejectReason('');

      // Adjust index if needed
      if (currentIndex >= newPending.length && newPending.length > 0) {
        setCurrentIndex(newPending.length - 1);
      }

      if (onApprovalComplete) {
        onApprovalComplete();
      }
    } catch (error) {
      console.error('Error rejecting media:', error);
      alert('Failed to reject media. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setShowRejectForm(false);
      setRejectReason('');
    }
  };

  const goToNext = () => {
    if (currentIndex < pendingMedia.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setShowRejectForm(false);
      setRejectReason('');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white rounded-xl p-8 max-w-md w-full">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
            <p className="text-gray-600">Loading pending media...</p>
          </div>
        </div>
      </div>
    );
  }

  if (pendingMedia.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white rounded-xl p-8 max-w-md w-full">
          <div className="text-center">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">All Caught Up!</h3>
            <p className="text-gray-600 mb-6">
              There are no pending media items to review.
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <Clock className="h-6 w-6 text-orange-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Media Approval</h2>
              <p className="text-sm text-gray-600">
                {currentIndex + 1} of {pendingMedia.length} pending items
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Media Preview */}
            <div className="space-y-4">
              <div className="bg-gray-100 rounded-lg overflow-hidden aspect-square flex items-center justify-center">
                {currentMedia.type === 'video' ? (
                  <video
                    src={currentMedia.url}
                    controls
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <img
                    src={currentMedia.url}
                    alt={currentMedia.fileName}
                    className="w-full h-full object-contain"
                  />
                )}
              </div>

              {/* Media Info */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  {currentMedia.type === 'video' ? (
                    <Film className="h-4 w-4 text-gray-600" />
                  ) : (
                    <ImageIcon className="h-4 w-4 text-gray-600" />
                  )}
                  <span className="font-medium text-gray-700">
                    {currentMedia.fileName}
                  </span>
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="font-medium">Gallery:</span>
                  <span>{currentMedia.galleryName}</span>
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="font-medium">Size:</span>
                  <span>{(currentMedia.size / 1024 / 1024).toFixed(2)} MB</span>
                </div>
              </div>
            </div>

            {/* Upload Details */}
            <div className="space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-orange-900 mb-1">
                      Pending Review
                    </h3>
                    <p className="text-sm text-orange-700">
                      This media was uploaded by a user and requires your approval before it becomes visible.
                    </p>
                  </div>
                </div>
              </div>

              {/* Uploader Info */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Uploader Information</h4>
                
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {currentMedia.uploaderName || 'Unknown User'}
                      </div>
                      <div className="text-sm text-gray-600">
                        {currentMedia.uploaderEmail}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-gray-600 pt-2 border-t">
                    <Calendar className="h-4 w-4" />
                    <span>Uploaded {formatDate(currentMedia.uploadedAt)}</span>
                  </div>
                </div>
              </div>

              {/* Metadata Preview */}
              {(currentMedia.metadata || currentMedia.title || currentMedia.description) && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900">Initial Metadata</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    {currentMedia.title && (
                      <div>
                        <span className="text-xs font-medium text-gray-500">Title</span>
                        <p className="text-sm text-gray-900">{currentMedia.title}</p>
                      </div>
                    )}
                    {currentMedia.description && (
                      <div>
                        <span className="text-xs font-medium text-gray-500">Description</span>
                        <p className="text-sm text-gray-900">{currentMedia.description}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Reject Form */}
              {showRejectForm && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900">Rejection Reason</h4>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Optional: Provide a reason for rejection..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                    rows={3}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          {/* Navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={goToPrevious}
              disabled={currentIndex === 0}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="text-sm text-gray-600 px-3">
              {currentIndex + 1} / {pendingMedia.length}
            </span>
            <button
              onClick={goToNext}
              disabled={currentIndex === pendingMedia.length - 1}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            {showRejectForm ? (
              <>
                <button
                  onClick={() => {
                    setShowRejectForm(false);
                    setRejectReason('');
                  }}
                  disabled={processing}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={processing}
                  className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {processing ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      <span>Rejecting...</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4" />
                      <span>Confirm Rejection</span>
                    </>
                  )}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setShowRejectForm(true)}
                  disabled={processing}
                  className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  <XCircle className="h-4 w-4" />
                  <span>Reject</span>
                </button>
                <button
                  onClick={handleApprove}
                  disabled={processing}
                  className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {processing ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      <span>Approving...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      <span>Approve</span>
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};